import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Search, MapPin, DollarSign, Heart, Bell, Building2, User, LogOut, Home, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const DashboardPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    spanish_level: '',
    sector: '',
    contract_type: ''
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.location) params.append('location', filters.location);
      if (filters.spanish_level) params.append('spanish_level', filters.spanish_level);
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.contract_type) params.append('contract_type', filters.contract_type);

      const response = await fetch(`${API}/jobs?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Error al cargar ofertas');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      localStorage.removeItem('token');
      setUser(null);
      navigate('/');
      toast.success('Sesión cerrada');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getSpanishLevelBadge = (level) => {
    const variants = {
      native: { color: 'bg-emerald-700 text-white', label: 'Nativo' },
      advanced: { color: 'bg-emerald-600 text-white', label: 'Avanzado' },
      intermediate: { color: 'bg-teal-600 text-white', label: 'Intermedio' },
      basic: { color: 'bg-teal-500 text-white', label: 'Básico' },
      'not-required': { color: 'bg-slate-200 text-slate-700', label: 'No requerido' }
    };
    const variant = variants[level] || variants['not-required'];
    return <Badge className={`${variant.color} font-medium`}>{variant.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-blue-900" />
              <span className="text-xl font-bold text-blue-900">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center space-x-2">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="home-nav-btn">
                  <Home className="w-4 h-4 mr-2" />
                  Inicio
                </Button>
              </Link>
              <Link to="/blog">
                <Button variant="ghost" size="sm" data-testid="blog-nav-btn">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Blog
                </Button>
              </Link>
              <Link to="/favorites">
                <Button variant="ghost" size="sm" data-testid="favorites-nav-btn">
                  <Heart className="w-4 h-4 mr-2" />
                  Favoritos
                </Button>
              </Link>
              <Link to="/alerts">
                <Button variant="ghost" size="sm" data-testid="alerts-nav-btn">
                  <Bell className="w-4 h-4 mr-2" />
                  Alertas
                </Button>
              </Link>
              <Link to="/companies">
                <Button variant="ghost" size="sm" data-testid="companies-nav-btn">
                  <Building2 className="w-4 h-4 mr-2" />
                  Empresas
                </Button>
              </Link>
              {user?.role === 'admin' && (
                <>
                  <Link to="/admin/jobs">
                    <Button variant="ghost" size="sm" data-testid="admin-jobs-nav-btn">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Admin Trabajos
                    </Button>
                  </Link>
                  <Link to="/blog/editor">
                    <Button variant="ghost" size="sm" data-testid="blog-editor-nav-btn">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Editor Blog
                    </Button>
                  </Link>
                  <Link to="/settings/adsense">
                    <Button variant="ghost" size="sm" data-testid="adsense-nav-btn">
                      <DollarSign className="w-4 h-4 mr-2" />
                      AdSense
                    </Button>
                  </Link>
                </>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Hola, {user?.name} 👋</h1>
          <p className="text-lg text-slate-600">Encuentra tu próxima oportunidad en Suiza</p>
        </div>

        {/* Search & Filters */}
        <Card className="mb-8 shadow-lg" data-testid="search-filters-card">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Buscar por título, empresa o descripción..."
                  className="pl-10 h-12"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  placeholder="Ubicación"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  data-testid="location-filter"
                />

                <Select value={filters.spanish_level} onValueChange={(value) => setFilters({ ...filters, spanish_level: value })}>
                  <SelectTrigger data-testid="spanish-level-filter">
                    <SelectValue placeholder="Nivel de español" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los niveles</SelectItem>
                    <SelectItem value="native">Nativo</SelectItem>
                    <SelectItem value="advanced">Avanzado</SelectItem>
                    <SelectItem value="intermediate">Intermedio</SelectItem>
                    <SelectItem value="basic">Básico</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  placeholder="Sector"
                  value={filters.sector}
                  onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                  data-testid="sector-filter"
                />

                <Select value={filters.contract_type} onValueChange={(value) => setFilters({ ...filters, contract_type: value })}>
                  <SelectTrigger data-testid="contract-type-filter">
                    <SelectValue placeholder="Tipo de contrato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="full-time">Tiempo completo</SelectItem>
                    <SelectItem value="part-time">Medio tiempo</SelectItem>
                    <SelectItem value="contract">Contrato</SelectItem>
                    <SelectItem value="freelance">Freelance</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={fetchJobs} className="w-full md:w-auto" data-testid="search-btn">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando ofertas...</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="jobs-list">
            {jobs.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-lg text-slate-600">No se encontraron ofertas con estos criterios</p>
              </Card>
            ) : (
              jobs.map((job) => (
                <Card
                  key={job.id}
                  className="hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-2 text-slate-900">{job.title}</h3>
                        <p className="text-lg font-medium text-blue-900 mb-3">{job.company}</p>

                        <div className="flex flex-wrap gap-3 mb-4">
                          <div className="flex items-center text-slate-600">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{job.location}</span>
                          </div>
                          {job.salary && (
                            <div className="flex items-center text-slate-600">
                              <DollarSign className="w-4 h-4 mr-1" />
                              <span>{job.salary}</span>
                            </div>
                          )}
                          <Badge variant="outline">{job.contract_type}</Badge>
                          <Badge variant="outline">{job.sector}</Badge>
                        </div>

                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-slate-600">Nivel de español:</span>
                          {getSpanishLevelBadge(job.spanish_level)}
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/jobs/${job.id}`);
                        }}
                        data-testid={`view-job-${job.id}`}
                      >
                        Ver detalles
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;